import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  ShoppingCart,
  Database,
  Warehouse,
  PackageCheck,
  Calculator,
  Truck,
  Settings,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { Button } from './ui/button';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/orders', label: 'Orders', icon: ShoppingCart },
  { path: '/asin-database', label: 'ASIN Database', icon: Database },
];

const inventoryItems = [
  { path: '/inventory/warehouse', label: 'Warehouse Stock', icon: Warehouse },
  { path: '/inventory/received', label: 'Received Products', icon: PackageCheck },
  { path: '/inventory/planner', label: 'Planner', icon: Calculator },
];

const otherItems = [
  { path: '/shipments', label: 'Shipments', icon: Truck },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        `sidebar-nav-item ${isActive ? 'active' : ''}`
      }
      onClick={() => setSidebarOpen(false)}
      data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
    >
      <item.icon size={18} strokeWidth={1.5} />
      <span>{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white border border-border rounded-md shadow-sm"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            GAUSS
          </h1>
          <p className="text-xs text-slate-500 mt-1">Supply Chain Management</p>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}

          <div className="sidebar-nav-group">
            <div className="sidebar-nav-group-label">Inventory</div>
            {inventoryItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>

          <div className="sidebar-nav-group">
            <div className="sidebar-nav-group-label">Operations</div>
            {otherItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="main-content">
        <div className="page-container">
          {children}
        </div>
      </main>
    </div>
  );
};
